import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Globe, Users, Eye, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const GeographyPanel = ({ hideFinancials = false }) => {
  const [geo, setGeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [geoTab, setGeoTab] = useState('visitors');

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/geo`, { withCredentials: true });
      setGeo(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) return <p className="text-white/40">Cargando datos geograficos...</p>;

  const tabs = [
    { id: 'visitors', label: 'Visitantes', icon: Eye },
    { id: 'buyers', label: 'Compradores', icon: Users },
    ...(!hideFinancials ? [{ id: 'revenue', label: 'Ingresos', icon: Globe }] : []),
  ];

  return (
    <div data-testid="geography-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Analiticas Geograficas</h2>
        <button onClick={load} className="p-2 hover:bg-white/10 rounded-lg text-white/40"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="glass-card p-4 text-center">
          <p className="text-2xl font-bold text-blue-400">{geo?.total_countries || 0}</p>
          <p className="text-xs text-white/50">Paises Visitantes</p>
        </div>
        <div className="glass-card p-4 text-center">
          <p className="text-2xl font-bold text-green-400">{geo?.total_buyer_countries || 0}</p>
          <p className="text-xs text-white/50">Paises Compradores</p>
        </div>
        {!hideFinancials && (
          <div className="glass-card p-4 text-center">
            <p className="text-2xl font-bold text-[#E8720C]">${(geo?.total_revenue || 0).toFixed(2)}</p>
            <p className="text-xs text-white/50">Ingresos Totales</p>
          </div>
        )}
      </div>

      <div className="flex gap-2 mb-6">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setGeoTab(tab.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors ${
              geoTab === tab.id ? 'bg-[#E8720C]/20 text-[#E8720C] border border-[#E8720C]/30' : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'
            }`} data-testid={`geo-tab-${tab.id}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {geoTab === 'visitors' && (
        <div className="glass-card p-6" data-testid="geo-visitors-panel">
          <h3 className="text-lg font-semibold mb-4">Visitantes por Pais</h3>
          {(!geo || !geo.visitors_by_country?.length) ? (
            <p className="text-white/40 text-sm">Sin datos de visitantes.</p>
          ) : (
            <div className="space-y-3">
              {geo.visitors_by_country.map((item, i) => {
                const max = geo.visitors_by_country[0]?.visits || 1;
                return (
                  <div key={i} className="flex items-center gap-4" data-testid={`geo-visitor-${i}`}>
                    <span className="w-6 text-xs text-white/30 text-right">{i + 1}</span>
                    <span className="w-28 text-sm font-medium truncate">{item.country}</span>
                    <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-blue-500/60 to-blue-400" style={{ width: `${(item.visits / max) * 100}%` }} />
                    </div>
                    <span className="text-sm text-white/60 w-20 text-right">{item.visits} visitas</span>
                    <span className="text-xs text-white/30 w-20 text-right">{item.unique_visitors || 0} unicos</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {geoTab === 'buyers' && (
        <div className="glass-card p-6" data-testid="geo-buyers-panel">
          <h3 className="text-lg font-semibold mb-4">Compradores por Pais</h3>
          {(!geo || !geo.buyers_by_country?.length) ? (
            <p className="text-white/40 text-sm">Sin datos de compradores.</p>
          ) : (
            <div className="space-y-3">
              {geo.buyers_by_country.map((item, i) => {
                const max = geo.buyers_by_country[0]?.purchases || 1;
                return (
                  <div key={i} className="flex items-center gap-4" data-testid={`geo-buyer-${i}`}>
                    <span className="w-6 text-xs text-white/30 text-right">{i + 1}</span>
                    <span className="w-28 text-sm font-medium truncate">{item.country}</span>
                    <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-green-500/60 to-green-400" style={{ width: `${(item.purchases / max) * 100}%` }} />
                    </div>
                    <span className="text-sm text-white/60 w-20 text-right">{item.purchases} ordenes</span>
                    <span className="text-xs text-white/30 w-20 text-right">{item.unique_buyers || 0} compradores</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {geoTab === 'revenue' && !hideFinancials && (
        <div className="glass-card p-6" data-testid="geo-revenue-panel">
          <h3 className="text-lg font-semibold mb-4">Ingresos por Pais</h3>
          {(!geo || !geo.buyers_by_country?.length) ? (
            <p className="text-white/40 text-sm">Sin datos de ingresos.</p>
          ) : (
            <div className="space-y-3">
              {[...geo.buyers_by_country].sort((a, b) => b.revenue - a.revenue).map((item, i) => {
                const max = [...geo.buyers_by_country].sort((a, b) => b.revenue - a.revenue)[0]?.revenue || 1;
                return (
                  <div key={i} className="flex items-center gap-4" data-testid={`geo-revenue-${i}`}>
                    <span className="w-6 text-xs text-white/30 text-right">{i + 1}</span>
                    <span className="w-28 text-sm font-medium truncate">{item.country}</span>
                    <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#E8720C]/60 to-[#E8720C]" style={{ width: `${(item.revenue / max) * 100}%` }} />
                    </div>
                    <span className="text-sm text-[#E8720C] font-semibold w-24 text-right">${item.revenue.toFixed(2)}</span>
                    <span className="text-xs text-white/30 w-20 text-right">{item.purchases} ordenes</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GeographyPanel;
